import { usePageModels } from '@/context/PageModels';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { useDataLayerSearchEvents } from '@/features/shared/Hooks/data-layer';
import { trackSearchClick } from '@/features/shared/Hooks/useElasticSearch';
import {
  MuiBox,
  MuiButton,
  MuiGrid,
  MuiPagination,
  MuiPaginationItem,
  MuiTypography,
} from '@/features/shared/material-ui';
import { SearchMeta } from '@/features/shared/SearchMeta';
import { IS_PREVIEW, LoadingStatusValues, Theme } from '@/utils/constants';
import {
  escapeHtml,
  formatText,
  getPageUrl,
  getResultLanguageURI,
  reduceText,
  setPageLink,
} from '@/utils/helpers';
import { resolveImageUrl } from '@/utils/resolvers';
import { BrPageContext } from '@bloomreach/react-sdk';
import clsx from 'clsx';
import React, { ChangeEvent, memo, useContext } from 'react';
import { Link } from 'react-router-dom';
import { CardResult } from '../Card';
import { ResultBlockPlaceholder } from './ResultBlock.placeholder';
import { getSolutionMarketplaceLabels, getSolutionMarketplaceTags } from './ResultsBlock.helpers';
import { ResultsBlockProps } from './ResultsBlock.interface';
import useStyles from './ResultsBlock.styles';

const ResultsBlock = ({
  theme = Theme.light,
  hasPagination = false,
  labels,
  config,
  isCached = false,
}: ResultsBlockProps) => {
  const classes = useStyles({});
  const page = useContext(BrPageContext);
  const { language } = usePageModels();

  const {
    results = [],
    pagination,
    queryParams,
    updateQueryParams,
    resetParams = () => null,
    status,
    labels: globalLabels = {},
    engine,
    requestID,
    searchKey = '',
  } = useContext(ElasticComponentContext);

  const { triggerSearchEventDataLayer } = useDataLayerSearchEvents('page_nav_click');

  const handlePagination = (event: ChangeEvent<unknown>, value: number) => {
    event.preventDefault();

    updateQueryParams('page', String(value));
    window.scrollTo({ top: 0, behavior: 'smooth' });
    triggerSearchEventDataLayer();
  };

  const NO_RESULTS_MESSAGE = formatText(
    globalLabels?.sorry_no_results || 'Sorry, we couldn’t find any results for “%1”',
    [escapeHtml(queryParams.q)],
  );

  const resetFilters = () => resetParams();

  const handleResultClicks = (evt: React.MouseEvent) => {
    const target = evt.target as HTMLElement;
    const a = target?.closest('a');

    if (a?.dataset?.resultid) {
      const { resultid } = a.dataset;
      trackSearchClick(searchKey, queryParams.q || '', engine, resultid, requestID);
    }
  };
  const rootClasses = clsx(classes.root, `${theme}-theme`);
  const paginationClasses = clsx(classes.pagination, 'gtm-pagination-container');
  const { loading } = LoadingStatusValues;
  const isLoading = React.useMemo(
    () => status === loading && !isCached,
    [status, loading, isCached],
  );

  if (IS_PREVIEW && !isCached) {
    return (
      <div className={rootClasses}>
        <div className={classes.content}>
          <ResultBlockPlaceholder animated={false} />
        </div>
      </div>
    );
  }

  return (
    <div className={rootClasses}>
      {isLoading ? (
        <div className={classes.content}>
          <ResultBlockPlaceholder />
        </div>
      ) : (
        <>
          {results.length > 0 ? (
            <>
              {/* This <div> is handling bubbled clicks */}
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
              <div className={classes.content} onClick={handleResultClicks}>
                <MuiGrid container spacing={3}>
                  {results.map((item: any, id: number) => {
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <MuiGrid key={id} item xs={12} sm={6} md={6} lg={4}>
                        <div className={clsx(classes.card, 'gtm-result')}>
                          <CardResult
                            url={
                              page &&
                              getPageUrl({ url: getResultLanguageURI(item, language), page })
                            }
                            cardHeading={item.title?.raw || ''}
                            cardDescription={reduceText(item.description?.raw || '')}
                            certified={/true/.test(item.is_genetec_certified?.raw)}
                            imageUrl={resolveImageUrl(item.image)}
                            labels={labels}
                            config={config}
                            tags={getSolutionMarketplaceTags({ item })}
                            resultLabels={getSolutionMarketplaceLabels({ item })}
                            resultID={item?.id?.raw}
                            engine={engine}
                          />
                        </div>
                      </MuiGrid>
                    );
                  })}
                </MuiGrid>
              </div>
              {hasPagination && pagination.totalPages > 1 && (
                <MuiPagination
                  data-datalayer-position="solution_marketplace_pagination"
                  className={paginationClasses}
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={handlePagination}
                  renderItem={(item: any) => (
                    <MuiPaginationItem component={Link} to={setPageLink(item.page)} {...item} />
                  )}
                />
              )}
              <SearchMeta paging={pagination} query={queryParams} />
            </>
          ) : (
            <div className={classes.root}>
              <MuiBox marginBottom={2}>
                <MuiTypography
                  variant="h5"
                  dangerouslySetInnerHTML={{ __html: NO_RESULTS_MESSAGE }}
                />
              </MuiBox>
              <MuiButton
                variant="outlined"
                color="secondary"
                onClick={resetFilters}
                className={classes.noResultsCta}
              >
                {globalLabels?.reset_filters || 'Reset all filters'}
              </MuiButton>
              <SearchMeta paging={pagination} query={queryParams} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default memo(ResultsBlock);
